import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, PageTitle, CommonCss, Card } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { Meta } from 'components/system/SystemPageChrome.styles';

import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert } from 'utils/toastBridge';
import AddressSearchDialog from 'components/common/AddressSearchDialog';
import {
  TENANT_STATUS_OPTIONS,
  createTenantUid,
  loadTenants,
  filterTenants,
  validateTenants,
  persistTenantsWithHistory,
  loadTenantHistory,
  formatBizRegNo,
  formatCorpRegNo,
} from 'master/tenantStore';
import { initTenantAppearanceFromCi } from 'system/appearanceStore';
import { normalizeHex } from 'styles/brandColorUtils';
import { useErpThemeOptional } from 'contexts/ErpThemeContext';

const TENANT_GRID_ID = 'system-tenant';
const HIST_GRID_ID = 'system-tenant-history';
const DETAIL_LABEL_W = '72px';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  flex: ${(p) => p.$flex || 1};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;


const BottomRow = styled.div`
  display: flex;
  gap: 8px;
  flex: ${(p) => p.$flex || 1};
  min-height: 0;
`;

const DetailBody = styled.div`
  padding: 10px 12px;
  overflow: auto;
  flex: 1;
  min-height: 0;
`;

/**
 * 시스템 운영 — 기관등록 (CM-DESIGN-001 cm_tenant PoC)
 *
 * - 시스템 전체 마스터(운영자)만 접근: 기관 생성 → 초기 관리자 계정 → 사용 설정의 시발점
 * - 회원가입이 없는 ERP 특성상 운영자가 기관·관리자 계정을 직접 만들어 준다
 * - 직인·로고는 로그인 기관 스코프 자산 → 환경설정에서 관리
 * - 현재는 시스템 마스터 계정 체계 도입 전이므로 masterYn=Y 사용자(DEMO·GNK admin)에게 노출
 */
const TenantRegisterInner = () => {
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const erpTheme = useErpThemeOptional();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) =>
      id === HIST_GRID_ID ? '이력은 조회 전용입니다. 기관 그리드에서 추가하세요.' : null,
    guardDelete: (id) => (id === HIST_GRID_ID ? '이력은 삭제할 수 없습니다.' : null),
  });
  const tenantGridRef = useRef(null);
  const histGridRef = useRef(null);
  const [selectedUid, setSelectedUid] = useState('');
  const selectedUidRef = useRef('');
  const [selectedHint, setSelectedHint] = useState(
    '기관을 선택하면 관리자 계정·이력이 표시됩니다. (직인은 환경설정)'
  );
  // 주소 검색 대상 행 (카카오 도로명주소)
  const [addressOpen, setAddressOpen] = useState(false);
  const addressTargetUidRef = useRef('');

  const searchDefaults = useMemo(
    () => ({ tenantCode: '', tenantNm: '', useYn: '' }),
    []
  );
  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues } = methods;

  // 선택 기관 상세(관리자 계정) — 저장 시 그리드 행에 반영
  const detailDefaults = useMemo(
    () => ({ tenantCodeDisp: '', adminId: '', adminNm: '', adminPwd: '' }),
    []
  );
  const detailMethods = useForm({ mode: 'onSubmit', defaultValues: detailDefaults });
  const { getValues: getDetailValues, reset: resetDetail } = detailMethods;

  const openAddressSearch = useCallback((row) => {
    if (!row?.tenantUid) return;
    addressTargetUidRef.current = row.tenantUid;
    setAddressOpen(true);
  }, []);

  const tenantColumns = useMemo(
    () => [
      hiCol.edTextColumnForKey({
        header: '기관코드',
        field: 'tenantCode',
        size: 110,
        required: true,
        title: '최초 등록 시에만 입력 가능하며 저장 후에는 변경할 수 없습니다.',
      }),
      hiCol.edTextColumn({ header: '기관명', field: 'tenantNm', size: 170, required: true }),
      hiCol.edNumberColumn({
        header: '회계기수',
        field: 'fiscalTerm',
        size: 90,
        align: 'center',
        title: '현재 기수 — 설립연도 입력 시 자동 계산 (기수·연도 이력은 CM-DESIGN-001 후속)',
      }),
      hiCol.edTextColumn({
        header: '설립연도',
        field: 'foundedYear',
        size: 90,
        align: 'center',
        title: '설립(1기) 연도 4자리 — 입력 시 회계기수 자동 계산',
      }),
      hiCol.edTextColumn({ header: 'GCC코드', field: 'gccCode', size: 100 }),
      hiCol.edTextColumn({
        header: '사업자번호',
        field: 'businessRegNo',
        size: 120,
        title: '숫자만 입력해도 xxx-xx-xxxxx로 표시 · 저장은 숫자만',
      }),
      hiCol.edTextColumn({
        header: '법인등록번호',
        field: 'corpRegNo',
        size: 140,
        title: '숫자만 입력해도 xxxxxx-xxxxxxx로 표시 · 저장은 숫자만',
      }),
      hiCol.edTextColumn({ header: '대표자명', field: 'ceoNm', size: 100 }),
      hiCol.edTextColumn({
        header: 'CI메인색',
        field: 'brandMainColor',
        size: 100,
        title: '기관 CI 메인 색상 (#RRGGBB). 저장 시 완화(soft) 후 표시·테마 초기값으로 적용',
      }),
      hiCol.edTextColumn({
        header: '주소',
        field: 'address',
        size: 260,
        showSearch: true,
        searchTitle: '도로명주소 검색 (카카오)',
        onSearch: ({ row }) => openAddressSearch(row),
      }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 70, options: TENANT_STATUS_OPTIONS }),
      hiCol.edNumberColumn({ header: '정렬', field: 'sortOrder', size: 70, align: 'center' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160 }),
    ],
    [openAddressSearch]
  );

  const histColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '변경일시', field: 'changeAtDisp', size: 140 }),
      hiCol.roTextColumn({ header: '유형', field: 'changeTypeNm', size: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '기관코드', field: 'tenantCodeDisp', size: 100 }),
      hiCol.roTextColumn({ header: '관리자ID', field: 'adminIdDisp', size: 90 }),
      hiCol.roTextColumn({ header: '직인', field: 'hasSealDisp', size: 60, align: 'center' }),
      hiCol.roTextColumn({ header: '처리자', field: 'changeBy', size: 90 }),
      hiCol.roTextColumn({ header: '메모', field: 'memo', size: 220 }),
    ],
    []
  );

  const tenantGrid = useHiGridHook({
    gridRef: tenantGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      tenantUid: '',
      tenantCode: '',
      tenantNm: '',
      fiscalTerm: 1,
      foundedYear: '',
      gccCode: '',
      businessRegNo: '',
      corpRegNo: '',
      address: '',
      ceoNm: '',
      sealImage: '',
      sealFileName: '',
      brandMainColor: '#2563EB',
      adminId: 'admin',
      adminNm: '관리자',
      adminPwd: '',
      useYn: 'Y',
      sortOrder: 10,
      remark: '',
    },
  });

  const histGrid = useHiGridHook({
    gridRef: histGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
    defaultRow: {},
  });

  /**
   * 상세 폼 값을 그리드 행에 반영 (행 전환·저장 전).
   * setData는 비동기(state)이므로 병합 결과 배열을 즉시 반환한다.
   */
  const applyDetailToRow = useCallback(
    (uid) => {
      const rows = tenantGridRef.current?.getData?.() || [];
      if (!uid) return rows;
      const idx = rows.findIndex((r) => r.tenantUid === uid);
      if (idx < 0) return rows;
      const d = getDetailValues();
      const cur = rows[idx];
      const next = {
        ...cur,
        adminId: d.adminId ?? cur.adminId,
        adminNm: d.adminNm ?? cur.adminNm,
        adminPwd: d.adminPwd ?? cur.adminPwd,
      };
      const changed =
        next.adminId !== cur.adminId ||
        next.adminNm !== cur.adminNm ||
        next.adminPwd !== cur.adminPwd;
      if (!changed) return rows;
      next.mod = cur.mod === 'A' ? 'A' : 'U';
      const merged = [...rows];
      merged[idx] = next;
      tenantGridRef.current?.setData?.(merged);
      return merged;
    },
    [getDetailValues]
  );

  const pushHistory = useCallback((uid) => {
    if (!uid) {
      histGridRef.current?.setData?.([]);
      return;
    }
    const list = loadTenantHistory(uid).map((h) => ({
      ...h,
      tenantCodeDisp: h.snapshot?.tenantCode || '',
      adminIdDisp: h.snapshot?.adminId || '',
      hasSealDisp: h.snapshot?.hasSeal || '',
    }));
    histGridRef.current?.setData?.(list);
  }, []);

  const selectTenantRow = useCallback(
    (row) => {
      if (!row?.tenantUid) return;
      let rows = tenantGridRef.current?.getData?.() || [];
      if (selectedUidRef.current && selectedUidRef.current !== row.tenantUid) {
        // 이전 행의 상세 편집분을 먼저 반영한 병합 결과 기준으로 로드
        rows = applyDetailToRow(selectedUidRef.current);
      }
      const cur = rows.find((r) => r.tenantUid === row.tenantUid) || row;
      selectedUidRef.current = cur.tenantUid;
      setSelectedUid(cur.tenantUid);
      resetDetail({
        tenantCodeDisp: cur.tenantCode || '',
        adminId: cur.adminId || '',
        adminNm: cur.adminNm || '',
        adminPwd: cur.adminPwd || '',
      });
      setSelectedHint(`${cur.tenantCode || '(코드미입력)'} ${cur.tenantNm || ''}`);
      pushHistory(cur.tenantUid);
    },
    [applyDetailToRow, resetDetail, pushHistory]
  );

  const handleSearch = useCallback(async () => {
    try {
      const all = await loadTenants();
      const filtered = filterTenants(all, getValues());
      tenantGridRef.current?.setData?.(filtered);
      const still = filtered.find((r) => r.tenantUid === selectedUidRef.current);
      if (still) {
        selectTenantRow(still);
      } else {
        selectedUidRef.current = '';
        setSelectedUid('');
        resetDetail({ tenantCodeDisp: '', adminId: '', adminNm: '', adminPwd: '' });
        setSelectedHint('기관을 선택하면 관리자 계정·이력이 표시됩니다. (직인은 환경설정)');
        pushHistory('');
      }
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
    }
  }, [getValues, selectTenantRow, resetDetail, pushHistory]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 최초 조회
  }, []);

  /**
   * 셀 저장 후처리
   * - 사업자번호/법인등록번호: 숫자만 입력해도 하이픈 포함 표시로 재포맷
   * - 설립연도: 4자리 연도 입력 시 회계기수 자동 계산 (올해 - 설립연도 + 1)
   */
  const handleAfterSaveCell = useCallback((row, { field, value }) => {
    if (!row?.tenantUid) return;
    const grid = tenantGridRef.current;
    if (!grid) return;
    const rows = grid.getData?.() || [];
    const idx = rows.findIndex((r) => r.tenantUid === row.tenantUid);
    if (idx < 0) return;

    if (field === 'businessRegNo') {
      const formatted = formatBizRegNo(value);
      if (formatted !== String(value ?? '')) grid.setCellValues?.(idx, { businessRegNo: formatted });
      return;
    }
    if (field === 'corpRegNo') {
      const formatted = formatCorpRegNo(value);
      if (formatted !== String(value ?? '')) grid.setCellValues?.(idx, { corpRegNo: formatted });
      return;
    }
    if (field === 'foundedYear') {
      const year = Number(String(value ?? '').trim());
      const nowYear = new Date().getFullYear();
      if (!/^(19|20)\d{2}$/.test(String(value ?? '').trim()) || year > nowYear) return;
      const term = nowYear - year + 1;
      if (Number(row.fiscalTerm) !== term) grid.setCellValues?.(idx, { fiscalTerm: term });
    }
  }, []);

  useEffect(() => {
    tenantGrid.onSelectRow = (row) => selectTenantRow(row);
    tenantGrid.onRowClicked = ({ data }) => selectTenantRow(data);
    tenantGrid.onAfterSaveCell = handleAfterSaveCell;
  }, [tenantGrid, selectTenantRow, handleAfterSaveCell]);

  /** 카카오 도로명주소 선택 → 대상 행 주소 반영 */
  const applyAddress = useCallback((payload) => {
    const uid = addressTargetUidRef.current;
    const grid = tenantGridRef.current;
    if (!uid || !grid || !payload?.address) return;
    const rows = grid.getData?.() || [];
    const idx = rows.findIndex((r) => r.tenantUid === uid);
    if (idx < 0) return;
    grid.setCellValues?.(idx, { address: payload.address });
  }, []);

  const handleAdd = useCallback(async () => {
    const data = tenantGridRef.current?.getData?.() || [];
    let max = 0;
    for (const r of data) {
      const n = Number(r?.sortOrder);
      if (Number.isFinite(n) && n > max) max = n;
    }
    const uid = createTenantUid();
    await tenantGrid.handleAdd({
      tenantUid: uid,
      id: uid,
      tenantCode: '',
      tenantNm: '',
      fiscalTerm: 1,
      foundedYear: '',
      gccCode: '',
      businessRegNo: '',
      corpRegNo: '',
      address: '',
      ceoNm: '',
      sealImage: '',
      sealFileName: '',
      adminId: 'admin',
      adminNm: '관리자',
      adminPwd: '',
      useYn: 'Y',
      sortOrder: Math.ceil((max + 1) / 10) * 10,
      remark: '',
    });
    const rows = tenantGridRef.current?.getData?.() || [];
    const row = rows.find((r) => r.tenantUid === uid);
    if (row) selectTenantRow(row);
  }, [tenantGrid, selectTenantRow]);

  const handleDelete = useCallback(async () => {
    await tenantGrid.handleDelete();
  }, [tenantGrid]);

  const handleSave = useCallback(async () => {
    const gridData = applyDetailToRow(selectedUidRef.current);
    const active = gridData.filter((r) => r.mod !== 'D');
    const v = validateTenants(active);
    if (!v.ok) {
      showAlert(v.message, 'warning');
      return;
    }

    try {
      const all = await loadTenants();
      const inFilterUids = new Set(filterTenants(all, getValues()).map((r) => r.tenantUid));
      const outside = all.filter((r) => !inFilterUids.has(r.tenantUid));
      const after = [...outside, ...gridData];

      const { saved, historyTouched } = await persistTenantsWithHistory(after, {
        userId: user?.id || user?.username || '',
      });

      saved.forEach((row) => {
        const code = String(row.tenantCode || '').trim();
        const hex = normalizeHex(row.brandMainColor);
        if (code && hex) {
          initTenantAppearanceFromCi(code, hex);
        }
      });
      erpTheme?.reload?.();

      tenantGridRef.current?.setData?.(filterTenants(saved, getValues()));
      const prevCode = String(
        gridData.find((r) => r.tenantUid === selectedUidRef.current)?.tenantCode || ''
      ).trim();
      const row =
        saved.find((r) => r.tenantUid === selectedUidRef.current) ||
        saved.find((r) => String(r.tenantCode || '').trim() === prevCode);
      if (row) selectTenantRow(row);
      showAlert(
        historyTouched > 0
          ? `저장했습니다. 이력 ${historyTouched}건이 기록되었습니다.\n로그인: 기관코드 / 관리자ID / 입력한 비밀번호`
          : '저장했습니다.',
        'success'
      );
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
    }
  }, [applyDetailToRow, getValues, user, selectTenantRow]);

  const searchFields = useMemo(
    () => [
      [
        // 멀티기관 관리 화면 — 기관코드 조회조건 노출 예외
        {
          name: 'tenantCode',
          label: '기관코드',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'tenantNm',
          label: '기관명',
          type: 'text',
          width: CommonCss.w120,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'useYn',
          label: '사용',
          type: 'select',
          width: CommonCss.w70,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...TENANT_STATUS_OPTIONS],
        },
      ],
    ],
    []
  );

  const detailFields = useMemo(
    () => [
      [
        {
          name: 'tenantCodeDisp',
          label: '기관코드',
          type: 'text',
          width: CommonCss.w110,
          labelWidth: DETAIL_LABEL_W,
          readonly: true,
        },
        {
          name: 'adminId',
          label: '관리자ID',
          type: 'text',
          width: CommonCss.w110,
          labelWidth: DETAIL_LABEL_W,
          required: true,
          title: '기관 초기 관리자(기관 마스터) 로그인 아이디',
        },
      ],
      [
        {
          name: 'adminNm',
          label: '관리자명',
          type: 'text',
          width: CommonCss.w110,
          labelWidth: DETAIL_LABEL_W,
        },
        {
          name: 'adminPwd',
          label: '비밀번호',
          type: 'password',
          width: CommonCss.w110,
          labelWidth: DETAIL_LABEL_W,
          title: '초기 비밀번호 (BE 연동 시 해시 저장·강제 변경 정책 적용)',
        },
      ],
    ],
    []
  );

  const getTenantExportData = useCallback(() => {
    const rows = tenantGridRef.current?.getFilteredData?.() ?? tenantGridRef.current?.getData?.() ?? [];
    // 직인 dataURL·비밀번호는 엑셀 제외
    return rows.map(({ sealImage: s, adminPwd, ...rest }) => rest);
  }, []);
  const getTenantExportCols = useCallback(() => tenantGridRef.current?.getDataColumns?.() ?? [], []);
  const getHistExportData = useCallback(
    () => histGridRef.current?.getFilteredData?.() ?? histGridRef.current?.getData?.() ?? [],
    []
  );
  const getHistExportCols = useCallback(() => histGridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: TENANT_GRID_ID,
    label: '기관',
    getData: getTenantExportData,
    getDataColumns: getTenantExportCols,
    joinKeys: ['tenantUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: HIST_GRID_ID,
    label: '기관이력',
    getData: getHistExportData,
    getDataColumns: getHistExportCols,
    joinKeys: ['histId'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '기관등록',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9)',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSearch, handleActiveAdd, handleActiveDelete, handleSave, openExcelDialog, activeLabel]
  );

  // 시스템 운영자 전용 — 일반 사용자는 메뉴에도 없지만 URL 진입도 차단
  if (!isMaster) {
    return (
      <PageContainer>
        <PageTitle>기관등록</PageTitle>
        <Meta>시스템 운영자(마스터) 전용 화면입니다. 접근 권한이 없습니다.</Meta>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <CollapsibleSearchPanel
          storageKey="system-tenant-register"
          prefsUserId={prefsUserId}
          summary="기관 조회 (운영자 전용)"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        활성 그리드: <strong style={{ color: theme.colors.primary }}>{activeLabel || '—'}</strong>
        {' · '}Alt+F2 순환{activeGridId ? ` · F8/F9 → ${activeLabel}` : ''}
        {' · '}운영자 {user?.name || user?.id || ''} ({tenantCode})
      </Meta>

      <Stack>
        <Panel $flex={1.3}>
          <ActiveGridHeader
            gridId={TENANT_GRID_ID}
            extra="시스템 운영자 전용 · 기관 생성 → 초기 관리자 → 사용 설정"
          >
            기관
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={TENANT_GRID_ID}
              label="기관"
              order={0}
              onAdd={handleAdd}
              onDelete={handleDelete}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={TENANT_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={tenantGridRef}
                  columns={tenantColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === TENANT_GRID_ID}
                  {...tenantGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="기관"
                  gridId={TENANT_GRID_ID}
                  prefsUserId={prefsUserId}
                  colOptions={tenantGrid.colOptions}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <BottomRow $flex={1}>
          <Panel $flex={0.9} style={{ maxWidth: 460 }}>
            <ActiveGridHeader extra="초기 관리자 계정">기관 상세</ActiveGridHeader>
            <Meta style={{ padding: '4px 12px' }}>{selectedHint}</Meta>
            <DetailBody>
              {selectedUid ? (
                <>
                  <FormProvider {...detailMethods}>
                    <FormArea fields={detailFields} dense defaultValues={detailDefaults} />
                  </FormProvider>
                  <Meta style={{ marginTop: 10 }}>
                    상세 변경은 저장(F10) 시 기관 행과 함께 반영됩니다.
                    직인·로고는 환경설정에서 관리합니다.
                  </Meta>
                </>
              ) : (
                <Meta>좌측 상단 그리드에서 기관을 선택하세요.</Meta>
              )}
            </DetailBody>
          </Panel>

          <Panel $flex={1.1}>
            <ActiveGridHeader gridId={HIST_GRID_ID} extra="신규·수정·관리자계정·중지">
              기관 이력
            </ActiveGridHeader>
            <GridBody>
              <ActiveGridScope
                gridId={HIST_GRID_ID}
                label="기관이력"
                order={1}
                canAdd={false}
                canDelete={false}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <GridExportFocusScope gridId={HIST_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                  <HiGridComponent
                    ref={histGridRef}
                    columns={histColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={48}
                    headerActive={activeGridId === HIST_GRID_ID}
                    {...histGrid.contextMenuProps}
                    enableHeaderMenu
                    enableColumnFilters
                    enableFreeze
                    enableQuickExport
                    exportFileName="기관이력"
                    gridId={HIST_GRID_ID}
                    prefsUserId={prefsUserId}
                  />
                </GridExportFocusScope>
              </ActiveGridScope>
            </GridBody>
          </Panel>
        </BottomRow>
      </Stack>

      <AddressSearchDialog
        open={addressOpen}
        onSelect={applyAddress}
        onClose={() => setAddressOpen(false)}
      />

      {excelDialog}
    </PageContainer>
  );
};

const TenantRegister = () => (
  <GridExportProvider>
    <ActiveGridProvider>
      <TenantRegisterInner />
    </ActiveGridProvider>
  </GridExportProvider>
);

export default TenantRegister;
